<!-- 图表区域组件 -->
<template>
  <div class="charts-section" v-loading="loading">
    <el-row :gutter="20">
      <!-- 工单趋势 -->
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span class="chart-title">工单趋势</span>
            <el-button type="text" icon="el-icon-download" @click="downloadChart('trend')">
              下载
            </el-button>
          </div>
          <div class="chart-container" ref="trendChart"></div>
        </el-card>
      </el-col>

      <!-- 工单状态分布 -->
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span class="chart-title">工单状态分布</span>
            <el-button type="text" icon="el-icon-download" @click="downloadChart('status')">
              下载
            </el-button>
          </div>
          <div class="chart-container" ref="statusChart"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" style="margin-top: 20px;">
      <!-- 工单类型对比 -->
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span class="chart-title">工单类型对比</span>
            <el-button type="text" icon="el-icon-download" @click="downloadChart('type')">
              下载
            </el-button>
          </div>
          <div class="chart-container" ref="typeChart"></div>
        </el-card>
      </el-col>

      <!-- 处理效率趋势 -->
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span class="chart-title">处理效率趋势</span>
            <el-button type="text" icon="el-icon-download" @click="downloadChart('efficiency')">
              下载
            </el-button>
          </div>
          <div class="chart-container" ref="efficiencyChart"></div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'ChartsSection',
  props: {
    dailyCountData: {
      type: Array,
      default: () => []
    },
    statusStatsData: {
      type: Object,
      default: () => ({})
    },
    dailyAvgHoursData: {
      type: Array,
      default: () => []
    },
    summaryData: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      charts: {}
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts();
    });
  },
  watch: {
    dailyCountData: {
      handler() {
        this.updateTrendChart();
      },
      deep: true
    },
    statusStatsData: {
      handler() {
        this.updateStatusChart();
      },
      deep: true
    },
    dailyAvgHoursData: {
      handler() {
        this.updateEfficiencyChart();
      },
      deep: true
    },
    summaryData: {
      handler() {
        this.updateTypeChart();
      },
      deep: true
    }
  },
  methods: {
    // 初始化图表
    initCharts() {
      this.initTrendChart();
      this.initStatusChart();
      this.initTypeChart();
      this.initEfficiencyChart();
    },

    // 工单趋势图表
    initTrendChart() {
      const chartDom = this.$refs.trendChart;
      this.charts.trend = echarts.init(chartDom);
      this.updateTrendChart();
    },

    updateTrendChart() {
      if (!this.charts.trend) return;

      const dates = this.dailyCountData.map(item => item.statDate);
      const inspectionData = this.dailyCountData.map(item => item.inspectionCount);
      const repairData = this.dailyCountData.map(item => item.repairCount);

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: ['维修工单', '巡检工单']
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: {
            formatter: (value) => {
              return value.split('-').slice(1).join('-');
            }
          }
        },
        yAxis: {
          type: 'value',
          name: '工单数量'
        },
        series: [
          {
            name: '维修工单',
            type: 'line',
            data: repairData,
            smooth: true,
            itemStyle: {
              color: '#409eff'
            }
          },
          {
            name: '巡检工单',
            type: 'line',
            data: inspectionData,
            smooth: true,
            itemStyle: {
              color: '#e6a23c'
            }
          }
        ]
      };

      this.charts.trend.setOption(option);
    },

    // 工单状态分布图表
    initStatusChart() {
      const chartDom = this.$refs.statusChart;
      this.charts.status = echarts.init(chartDom);
      this.updateStatusChart();
    },

    updateStatusChart() {
      if (!this.charts.status || !this.statusStatsData.overallStatusStats) return;

      const data = this.statusStatsData.overallStatusStats.map(item => ({
        value: item.count,
        name: item.statusName
      }));

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          data: data.map(item => item.name)
        },
        series: [
          {
            name: '工单状态',
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['60%', '50%'],
            data: data,
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            label: {
              formatter: '{b}: {c}'
            }
          }
        ]
      };

      this.charts.status.setOption(option);
    },

    // 工单类型对比图表
    initTypeChart() {
      const chartDom = this.$refs.typeChart;
      this.charts.type = echarts.init(chartDom);
      this.updateTypeChart();
    },

    updateTypeChart() {
      if (!this.charts.type) return;

      const summary = this.summaryData || {};
      const repairTotal = Number(summary.repairTotalCount) || 0;
      const inspectionTotal = Number(summary.inspectionTotalCount) || 0;
      const repairCompleted = Number(summary.repairCompletedCount) || 0;
      const inspectionCompleted = Number(summary.inspectionCompletedCount) || 0;
      const repairAvg = summary.repairAvgHours != null ? parseFloat(Number(summary.repairAvgHours).toFixed(2)) : 0;
      const inspectionAvg = summary.inspectionAvgHours != null ? parseFloat(Number(summary.inspectionAvgHours).toFixed(2)) : 0;

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: ['总数', '完成数', '平均处理时长']
        },
        xAxis: {
          type: 'category',
          data: ['维修工单', '巡检工单']
        },
        yAxis: [
          {
            type: 'value',
            name: '工单数量',
            position: 'left'
          },
          {
            type: 'value',
            name: '平均处理时长(h)',
            position: 'right'
          }
        ],
        series: [
          {
            name: '总数',
            type: 'bar',
            data: [repairTotal, inspectionTotal],
            itemStyle: {
              color: '#409eff'
            }
          },
          {
            name: '完成数',
            type: 'bar',
            data: [repairCompleted, inspectionCompleted],
            itemStyle: {
              color: '#67c23a'
            }
          },
          {
            name: '平均处理时长(h)',
            type: 'line',
            yAxisIndex: 1,
            data: [repairAvg, inspectionAvg],
            itemStyle: {
              color: '#e6a23c'
            }
          }
        ]
      };

      this.charts.type.setOption(option);
    },

    // 处理效率趋势图表
    initEfficiencyChart() {
      const chartDom = this.$refs.efficiencyChart;
      this.charts.efficiency = echarts.init(chartDom);
      this.updateEfficiencyChart();
    },

    updateEfficiencyChart() {
      if (!this.charts.efficiency) return;
      const dates = this.dailyAvgHoursData.map(item => item.statDate);
      const repairHours = this.dailyAvgHoursData.map(item => item.repairAvgHours);
      const inspectionHours = this.dailyAvgHoursData.map(item => item.inspectionAvgHours);

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: ['维修工单', '巡检工单']
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: {
            formatter: (value) => {
              return value.split('-').slice(1).join('-');
            }
          }
        },
        yAxis: {
          type: 'value',
          name: '平均处理时长(h)'
        },
        series: [
          {
            name: '维修工单',
            type: 'line',
            data: repairHours,
            smooth: true,
            itemStyle: {
              color: '#409eff'
            }
          },
          {
            name: '巡检工单',
            type: 'line',
            data: inspectionHours,
            smooth: true,
            itemStyle: {
              color: '#e6a23c'
            }
          }
        ]
      };

      this.charts.efficiency.setOption(option);
    },

    // 下载图表
    downloadChart(type) {
      if (this.charts[type]) {
        const url = this.charts[type].getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff'
        });
        const link = document.createElement('a');
        link.download = `${type}-chart.png`;
        link.href = url;
        link.click();
      }
    }
  },

  beforeDestroy() {
    // 销毁图表实例
    Object.values(this.charts).forEach(chart => {
      if (chart) {
        chart.dispose();
      }
    });
  }
};
</script>

<style lang="scss" scoped>
.charts-section {
  .chart-card {
    height: 400px;

    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .chart-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }
    }

    .chart-container {
      width: 100%;
      height: 300px;
    }
  }
}
</style>
